import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { ArrowUpRight, MapPin, Phone, Mail } from 'lucide-react'

export default function Contact() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section 
      id="contact" 
      ref={ref} 
      className="relative bg-[#FDFBF7] dark:bg-neutral-950 py-32 overflow-hidden transition-colors duration-500"
      aria-labelledby="contact-heading"
      itemScope
      itemType="https://schema.org/ContactPage"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          className="mb-20"
        >
          <span className="text-rose-500 font-mono text-xs uppercase tracking-[0.3em] block mb-6">
            Contact
          </span>
        </motion.div>

        <div className="max-w-4xl" itemScope itemType="https://schema.org/EducationalOrganization">
          <meta itemProp="name" content="Sudha's Academy" />
          <meta itemProp="url" content="https://sudhasacademy.com" />
          
          {/* Main Heading */}
          <motion.h2
            id="contact-heading"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1 }}
            className="text-6xl md:text-8xl font-serif font-black text-charcoal-900 dark:text-white leading-[0.85] mb-16"
          >
            Let's<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-rose-600">
              Talk
            </span>
          </motion.h2>

          {/* Contact Links */}
          <address className="space-y-0 not-italic">
            <motion.a
              href="mailto:hello@sudhasacademy.com"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="group flex items-center justify-between py-8 border-t border-charcoal-200 dark:border-white/10 hover:border-charcoal-400 dark:hover:border-white/30 transition-colors"
              itemProp="email"
              aria-label="Email us at hello@sudhasacademy.com"
            >
              <div className="flex items-center gap-6">
                <Mail className="w-6 h-6 text-rose-500" aria-hidden="true" />
                <div>
                  <div className="text-[10px] text-charcoal-400 dark:text-neutral-500 font-mono uppercase tracking-[0.3em] mb-2">Email</div>
                  <div className="text-charcoal-900 dark:text-white text-xl md:text-2xl">hello@sudhasacademy.com</div>
                </div>
              </div>
              <ArrowUpRight className="w-6 h-6 text-charcoal-300 dark:text-neutral-600 group-hover:text-charcoal-900 dark:group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" aria-hidden="true" />
            </motion.a>

            <motion.a
              href="tel:+919876543210"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="group flex items-center justify-between py-8 border-t border-charcoal-200 dark:border-white/10 hover:border-charcoal-400 dark:hover:border-white/30 transition-colors"
              itemProp="telephone"
              aria-label="Call us at +91 98765 43210"
            >
              <div className="flex items-center gap-6">
                <Phone className="w-6 h-6 text-rose-500" aria-hidden="true" />
                <div>
                  <div className="text-[10px] text-charcoal-400 dark:text-neutral-500 font-mono uppercase tracking-[0.3em] mb-2">Phone</div>
                  <div className="text-charcoal-900 dark:text-white text-xl md:text-2xl">+91 98765 43210</div>
                </div>
              </div>
              <ArrowUpRight className="w-6 h-6 text-charcoal-300 dark:text-neutral-600 group-hover:text-charcoal-900 dark:group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" aria-hidden="true" />
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-6 py-8 border-t border-b border-charcoal-200 dark:border-white/10"
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
            >
              <MapPin className="w-6 h-6 text-rose-500" aria-hidden="true" />
              <div>
                <div className="text-[10px] text-charcoal-400 dark:text-neutral-500 font-mono uppercase tracking-[0.3em] mb-2">Address</div>
                <div className="text-charcoal-600 dark:text-neutral-300 text-lg">
                  <span itemProp="streetAddress">13, Shermin Shahib St</span>, 
                  <span itemProp="addressLocality"> Tiruppur</span>, 
                  <span itemProp="addressRegion"> Tamil Nadu</span>, 
                  <span itemProp="addressCountry"> India</span>
                </div>
              </div>
            </motion.div>
          </address>
          
          {/* Hidden geo coordinates for SEO */}
          <div className="hidden" itemProp="geo" itemScope itemType="https://schema.org/GeoCoordinates">
            <meta itemProp="latitude" content="11.1085" />
            <meta itemProp="longitude" content="77.3411" />
          </div>
        </div>
      </div>
    </section>
  )
}
